import React, { useMemo, useEffect, useRef } from 'react';
import styles from './UIGamePlayerInformationContainer.module.scss';
import { UIGamePlayerInformation } from './UIGamePlayerInformation.tsx';
import { PlayerColor, CardEnum, TurnState, TradeResponseStatus } from '@colonist/game-states';
import { CardValidatorOrdered, Point } from '@colonist/game-mechanic-validators';
import clsx from 'clsx';
import { useGameStore } from '../../store/GameStoreContext.ts';
import { usePlayerValidators } from '../../store/selectors/usePlayerValidators.ts';
import { UIGameDiceGroupContainer } from '../../common/UIGameDiceGroup/UIGameDiceGroupContainer.tsx';
import { useCurrentStateValidator } from '../../store/selectors/useCurrentStateValidator.ts';
import { ModeSettingEnum } from '@colonist/types';
import { useMechanicValidatorKnight } from '../../store/selectors/useMechanicValidatorKnight.ts';
import { useMechanicValidatorLargestArmy } from '../../store/selectors/useMechanicValidatorLargestArmy.ts';
import { useMechanicValidatorLongestRoad } from '../../store/selectors/useMechanicValidatorLongestRoad.ts';
import SimpleBarImport from 'simplebar-react';
const SimpleBar = SimpleBarImport;
export const getGlobalPositionOfPlayerCard = (playerColor, isResourceCard)=>{
    const playerElement = document.querySelector(`[data-player-color="${playerColor}"]`);
    if (!playerElement) return undefined;
    const uiGameContainer = document.getElementById('ui-game');
    if (uiGameContainer == undefined) return undefined;
    const uiGameRect = uiGameContainer.getBoundingClientRect();
    const dataTag = isResourceCard ? '[data-resource-card]' : '[data-development-card]';
    const cardElement = playerElement.querySelector(dataTag);
    if (!cardElement) return undefined;
    const cardElementRect = cardElement.getBoundingClientRect();
    return new Point(cardElementRect.left - uiGameRect.left, cardElementRect.top - uiGameRect.top);
};
export const getOpponentPlayerOrderWhereMyColorIsLast = (playOrder, myColor)=>{
    if (myColor === PlayerColor.None) return playOrder;
    const newOrder = [];
    const myColorIndex = playOrder.indexOf(myColor);
    const numberOfPlayers = playOrder.length;
    const indexOfPlayerAfterMe = myColorIndex + 1;
    for(let index = indexOfPlayerAfterMe; index <= numberOfPlayers + myColorIndex; index++){
        newOrder.push(playOrder[index % numberOfPlayers]);
    }
    return newOrder.slice(0, -1);
};
/**
 * @deprecated Use useIsPlayerTakingAction instead
 */ const isPlayerTakingOutOfTurnAction = (playerColor, gameState)=>{
    const currentPlayerState = gameState.playerStates[playerColor];
    if (currentPlayerState == undefined) return false;
    if (currentPlayerState.isTakingAction) return true;
    const isWaitingForOpponentTradeResponses = Object.keys(gameState.tradeState.activeOffers).length > 0 && gameState.currentState.currentTurnPlayerColor === playerColor;
    if (isWaitingForOpponentTradeResponses) return true;
    for (const activeTradeState of Object.values(gameState.tradeState.activeOffers)){
        if (activeTradeState == undefined) continue;
        if (activeTradeState.playersCreatingCounterOffer[playerColor] === true) return true;
        if (activeTradeState.playerResponses[playerColor] === TradeResponseStatus.PendingResponse) return true;
    }
    return false;
};
const scrollToCurrentTurnPlayer = (turnState, currentTurnPlayerColor, simplebarRef, shouldAnimateScroll = true)=>{
    const opponentsContainer = simplebarRef.contentWrapperEl;
    if (opponentsContainer == undefined) return;
    if (turnState !== TurnState.Dice) return;
    const isScrollable = opponentsContainer.scrollHeight > opponentsContainer.clientHeight;
    if (!isScrollable) return;
    const currentTurnPlayerElement = opponentsContainer.querySelector(`[data-player-color="${currentTurnPlayerColor}"]`);
    if (currentTurnPlayerElement == undefined) return;
    const contentEl = simplebarRef.contentEl;
    if (contentEl == undefined) return;
    const playerIndex = Array.from(contentEl.children).indexOf(currentTurnPlayerElement);
    const itemHeight = currentTurnPlayerElement.clientHeight;
    const gap = 0.2 * 16;
    const scrollIndex = Math.max(0, playerIndex - 1);
    const scrollDestination = scrollIndex * (itemHeight + gap);
    if (shouldAnimateScroll) {
        opponentsContainer.scrollTo({
            top: scrollDestination,
            behavior: 'smooth'
        });
        return;
    } else {
        opponentsContainer.scrollTop = scrollDestination;
    }
};
export const UIGamePlayerInformationContainer = ({ className })=>{
    const gameState = useGameStore((state)=>state.gameState);
    const currentStateValidator = useCurrentStateValidator();
    const isMyColorSpectator = useGameStore((state)=>state.isMyColorSpectator);
    const myColor = useGameStore((state)=>state.myColor);
    const modeSetting = useGameStore((state)=>state.gameSettings.modeSetting);
    const victoryPointsToWin = useGameStore((state)=>state.gameSettings.victoryPointsToWin);
    const userStates = useGameStore((state)=>state.gameUserStates.userStates);
    const playOrder = useGameStore((state)=>state.gameDetails.playOrder);
    const { getPlayerValidator } = usePlayerValidators();
    const translationKeyContext = useGameStore((state)=>state.gameMode.translationKeyContext);
    const simplebarRef = useRef(null);
    useEffect(()=>{
        const { turnState, currentTurnPlayerColor } = gameState.currentState;
        const shouldScrollToCurrentTurnPlayer = turnState === TurnState.Dice && currentTurnPlayerColor !== myColor;
        if (shouldScrollToCurrentTurnPlayer && simplebarRef.current) {
            scrollToCurrentTurnPlayer(turnState, currentTurnPlayerColor, simplebarRef.current, true);
        }
    }, [
        gameState.currentState.turnState,
        gameState.currentState.currentTurnPlayerColor,
        myColor
    ]);
    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P;
    const effectiveMyColor = isMyColorSpectator ? playOrder[0] : myColor;
    const longestRoadValidator = useMechanicValidatorLongestRoad();
    const largestArmyValidator = useMechanicValidatorLargestArmy();
    const knightValidator = useMechanicValidatorKnight();
    const merchantState = gameState.mechanicMerchantState;
    const createPlayerInformationComponent = (playerColor, containerClassName)=>{
        var _gameState_mechanicDevelopmentCardsState, _gameState_mechanicProgressCardsState, _gameState_mechanicCityImprovementsState;
        const playerValidator = getPlayerValidator(playerColor);
        if (playerValidator == undefined) return undefined;
        const isCurrentUser = playerColor === effectiveMyColor;
        const resourceCards = new CardValidatorOrdered(playerValidator.resourceCards);
        const developmentCardsState = (_gameState_mechanicDevelopmentCardsState = gameState.mechanicDevelopmentCardsState) === null || _gameState_mechanicDevelopmentCardsState === void 0 ? void 0 : _gameState_mechanicDevelopmentCardsState.players[playerColor];
        var _developmentCardsState_developmentCards;
        const developmentCards = new CardValidatorOrdered((_developmentCardsState_developmentCards = developmentCardsState === null || developmentCardsState === void 0 ? void 0 : developmentCardsState.developmentCards) !== null && _developmentCardsState_developmentCards !== void 0 ? _developmentCardsState_developmentCards : {
            cards: []
        });
        const progressCardsState = (_gameState_mechanicProgressCardsState = gameState.mechanicProgressCardsState) === null || _gameState_mechanicProgressCardsState === void 0 ? void 0 : _gameState_mechanicProgressCardsState.players[playerColor];
        var _progressCardsState_progressCards;
        const progressCards = new CardValidatorOrdered((_progressCardsState_progressCards = progressCardsState === null || progressCardsState === void 0 ? void 0 : progressCardsState.progressCards) !== null && _progressCardsState_progressCards !== void 0 ? _progressCardsState_progressCards : {
            cards: []
        });
        var _gameState_mechanicCityImprovementsState_playerColor;
        const cityImprovementState = (_gameState_mechanicCityImprovementsState_playerColor = (_gameState_mechanicCityImprovementsState = gameState.mechanicCityImprovementsState) === null || _gameState_mechanicCityImprovementsState === void 0 ? void 0 : _gameState_mechanicCityImprovementsState[playerColor]) !== null && _gameState_mechanicCityImprovementsState_playerColor !== void 0 ? _gameState_mechanicCityImprovementsState_playerColor : {};
        const longestRoadPlayerState = longestRoadValidator === null || longestRoadValidator === void 0 ? void 0 : longestRoadValidator.getLongestRoadPlayerState(playerColor);
        var _longestRoadPlayerState_longestRoad;
        const longestRoadCount = (_longestRoadPlayerState_longestRoad = longestRoadPlayerState === null || longestRoadPlayerState === void 0 ? void 0 : longestRoadPlayerState.longestRoad) !== null && _longestRoadPlayerState_longestRoad !== void 0 ? _longestRoadPlayerState_longestRoad : 0;
        const hasLongestRoad = (longestRoadValidator === null || longestRoadValidator === void 0 ? void 0 : longestRoadValidator.getPlayerWithLongestRoadCard()) === playerColor;
        const hasLargestArmy = (largestArmyValidator === null || largestArmyValidator === void 0 ? void 0 : largestArmyValidator.getPlayerWithLargestArmy()) === playerColor;
        const knightPlayerValidator = knightValidator === null || knightValidator === void 0 ? void 0 : knightValidator.getKnightPlayerValidator(playerColor);
        var _knightPlayerValidator_getPlayerKnightStrength;
        const knightStrength = (_knightPlayerValidator_getPlayerKnightStrength = knightPlayerValidator === null || knightPlayerValidator === void 0 ? void 0 : knightPlayerValidator.getPlayerKnightStrength()) !== null && _knightPlayerValidator_getPlayerKnightStrength !== void 0 ? _knightPlayerValidator_getPlayerKnightStrength : 0;
        const doesEveryPlayerHaveZeroKnightStrength = (knightValidator === null || knightValidator === void 0 ? void 0 : knightValidator.getTotalKnightStrength()) === 0;
        var _knightValidator_getPlayersWithHighestKnightStrength_includes;
        const hasHighestKnightStrength = doesEveryPlayerHaveZeroKnightStrength ? false : (_knightValidator_getPlayersWithHighestKnightStrength_includes = knightValidator === null || knightValidator === void 0 ? void 0 : knightValidator.getPlayersWithHighestKnightStrength().includes(playerColor)) !== null && _knightValidator_getPlayersWithHighestKnightStrength_includes !== void 0 ? _knightValidator_getPlayersWithHighestKnightStrength_includes : false;
        var _developmentCardsState_developmentCardsUsed;
        const developmentCardsUsed = (_developmentCardsState_developmentCardsUsed = developmentCardsState === null || developmentCardsState === void 0 ? void 0 : developmentCardsState.developmentCardsUsed) !== null && _developmentCardsState_developmentCardsUsed !== void 0 ? _developmentCardsState_developmentCardsUsed : [];
        const knightDevelopmentCardsUsedCount = developmentCardsUsed.filter((card)=>card === CardEnum.Knight).length;
        const hasMerchant = (merchantState === null || merchantState === void 0 ? void 0 : merchantState.owner) === playerColor;
        const hasRobber = currentStateValidator.nextPlayerToMoveRobber === playerColor;
        const isCurrentPlayerTurn = currentStateValidator.currentTurnPlayers.includes(playerColor);
        const hasSpecialBuildPhaseFlag = gameState.currentState.turnState === TurnState.SpecialBuildPhase && isCurrentPlayerTurn;
        const isTakingAction = isPlayerTakingOutOfTurnAction(playerColor, gameState) && playerColor !== myColor;
        const diceComponent = !isCurrentUser && modeSetting !== ModeSettingEnum.ColonistRush4P ? /*#__PURE__*/ React.createElement(UIGameDiceGroupContainer, {
            className: styles.diceGroup,
            playerColor: playerColor,
            size: "medium",
            disableDiceSound: true
        }) : null;
        const playerInfo = /*#__PURE__*/ React.createElement(UIGamePlayerInformation, {
            className: styles.playerInformation,
            amountOfResourceCards: resourceCards.getNumberOfCardsInInventory(),
            cardDiscardLimit: playerValidator.cardDiscardLimit,
            cityImprovementState: cityImprovementState,
            developmentCards: developmentCards,
            gameMode: modeSetting,
            hasHighestKnightStrength: hasHighestKnightStrength,
            hasLargestArmy: hasLargestArmy,
            hasLongestRoad: hasLongestRoad,
            hasMerchant: hasMerchant,
            hasSpecialBuildPhaseFlag: hasSpecialBuildPhaseFlag,
            isActive: isCurrentPlayerTurn,
            isCurrentUser: isCurrentUser,
            isTakingAction: isTakingAction,
            knightDevelopmentCardsUsedCount: knightDevelopmentCardsUsedCount,
            knightStrength: knightStrength,
            longestRoadCount: longestRoadCount,
            playerValidator: playerValidator,
            progressCards: progressCards,
            translationContext: translationKeyContext,
            victoryPointsToWin: victoryPointsToWin,
            hasRobber: hasRobber
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: containerClassName,
            "data-player-color": playerColor,
            key: playerColor
        }, diceComponent, playerInfo);
    };
    const opponentPlayerComponents = useMemo(()=>{
        const playerExceptMyColor = getOpponentPlayerOrderWhereMyColorIsLast(playOrder, effectiveMyColor);
        const opponentComponents = playerExceptMyColor.map((playerColor)=>createPlayerInformationComponent(playerColor, clsx(styles.opponentPlayerRow, styles.playerRow)));
        return opponentComponents;
    }, [
        gameState,
        userStates,
        myColor,
        modeSetting,
        victoryPointsToWin,
        playOrder,
        isMyColorSpectator,
        currentStateValidator
    ]);
    const mainUserComponent = createPlayerInformationComponent(effectiveMyColor, styles.playerRow);
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.container, className),
        "data-player-information-container": true
    }, /*#__PURE__*/ React.createElement(SimpleBar, {
        ref: simplebarRef,
        className: clsx(styles.opponentsScrollContainer, {
            [styles.beginnerMode]: isBeginnerMode
        }),
        classNames: {
            contentEl: styles.opponentsScrollContainerScrollContent
        }
    }, opponentPlayerComponents), mainUserComponent);
};
